/**
 * Shared UI atoms for the Maternal companion.
 *
 * Keeping these tiny + consistent matters more than re-using shadcn primitives
 * here — every maternal screen must feel calm, soft, and emotionally safe.
 * If the design changes, it changes once, in this file.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, Heart } from 'lucide-react';

/** Back-to-hub link. Top-of-page navigation primitive — identical on every
 *  maternal sub-route so users always know how to step back. */
export const MaternalBackLink = ({ to = '/maternal-health', label = 'Back to Maternal Health' }) => {
  const navigate = useNavigate();
  return (
    <button
      onClick={() => navigate(to)}
      className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-rose-700 mb-4 motion-safe:transition-colors"
      data-testid="maternal-back-link"
    >
      <ArrowLeft className="h-4 w-4" />
      {label}
    </button>
  );
};

/** Calm full-page loader. Used while fetching for any maternal page so
 *  users see a single, consistent waiting state. */
export const MaternalLoader = ({ label = 'Just a moment…', testid = 'maternal-loading' }) => (
  <div
    className="min-h-screen bg-gradient-to-b from-rose-50/40 via-white to-white flex items-center justify-center"
    data-testid={testid}
  >
    <div className="flex flex-col items-center gap-2 text-rose-600">
      <Loader2 className="h-6 w-6 animate-spin" />
      <p className="text-sm text-gray-500">{label}</p>
    </div>
  </div>
);

/** Footer disclaimer — identical wording on every maternal page so the
 *  preventive-not-diagnostic boundary is unmistakable. */
export const MaternalDisclaimer = () => (
  <p
    className="text-[11px] text-gray-400 text-center mt-6"
    data-testid="maternal-disclaimer"
  >
    HealthLoop supports awareness — it does not diagnose or replace maternity care.
  </p>
);

/** Soft empty / locked state. A single calm card with optional icon, title,
 *  body and CTA. Replaces the per-page bespoke empties so the tone is
 *  identical everywhere. */
export const MaternalEmptyState = ({
  icon: Icon = Heart,
  title,
  body,
  ctaLabel,
  onCta,
  testid = 'maternal-empty-state',
}) => (
  <div
    className="rounded-2xl border-2 border-dashed border-gray-200 bg-white p-6 text-center"
    data-testid={testid}
  >
    <Icon className="h-7 w-7 text-gray-400 mx-auto mb-2" />
    <p className="text-sm text-gray-700 font-semibold">{title}</p>
    {body && <p className="text-xs text-gray-500 mt-1 leading-relaxed max-w-sm mx-auto">{body}</p>}
    {ctaLabel && onCta && (
      <button
        onClick={onCta}
        className="mt-3 inline-flex items-center gap-1 bg-rose-600 hover:bg-rose-700 text-white text-sm font-semibold rounded-xl px-4 h-11 motion-safe:transition-colors"
      >
        {ctaLabel}
      </button>
    )}
  </div>
);

/** Standard page shell — gradient background + bottom-nav clearance + shared
 *  max-width / padding. Wrap every maternal sub-route in this so spacing
 *  stays identical across screens. */
export const MaternalPageShell = ({ children, testid }) => (
  <div
    className="min-h-screen bg-gradient-to-b from-rose-50/40 via-white to-white pb-24"
    data-testid={testid}
  >
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6">{children}</div>
  </div>
);
